import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowUp,
  CloudUpload,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, jsonBody } from './api'
import {
  flattenLayoutVersions,
  PlaylistPreview,
  type Layout,
  type MediaAsset,
  type Playlist,
  type SlideTransition,
  type Ticker,
} from './content-pages'
import type { Overlay } from './overlay-preview'
import type { PlaylistItemMode } from './playlist-rules'
import { SignageFontSelect } from './signage-fonts'
import {
  createTickerRow,
  DynamicTickerBar,
  type PrayerTimes,
  type TickerAnimation,
  type TickerBackgroundMode,
  type TickerRow,
  type TickerRowType,
} from './ticker-preview'
import type { Page } from './types'
import { EmptyState, PageHeader, TableShell } from './ui'

interface PlaylistEditorItem {
  slideVersionId: string
  durationMs: number
  transition: SlideTransition
  mode: PlaylistItemMode
  repeatEvery: number | null
  activeStartTime: string | null
  activeEndTime: string | null
  activeTimezone: string | null
}

interface Publication {
  version: number
  fallbackPlaylistId: string
  targeted: boolean
}

const newItem = (): PlaylistEditorItem => ({
  slideVersionId: '',
  durationMs: 10000,
  transition: 'FADE',
  mode: 'NORMAL',
  repeatEvery: null,
  activeStartTime: null,
  activeEndTime: null,
  activeTimezone: null,
})

export function PlaylistsPage() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [tickerId, setTickerId] = useState('')
  const [overlayId, setOverlayId] = useState('')
  const [leftOverlayId, setLeftOverlayId] = useState('')
  const [rightOverlayId, setRightOverlayId] = useState('')
  const [items, setItems] = useState<PlaylistEditorItem[]>([newItem()])
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [publishPlaylistId, setPublishPlaylistId] = useState('')
  const playlists = useQuery({
    queryKey: ['playlists'],
    queryFn: () => api<Playlist[]>('/playlists'),
    refetchOnMount: 'always',
  })
  const publications = useQuery({
    queryKey: ['publications'],
    queryFn: () => api<Publication[]>('/publications'),
  })
  const layouts = useQuery({
    queryKey: ['layouts'],
    queryFn: () => api<Layout[]>('/layouts'),
  })
  const tickers = useQuery({
    queryKey: ['tickers'],
    queryFn: () => api<Ticker[]>('/tickers'),
  })
  const overlays = useQuery({
    queryKey: ['overlays'],
    queryFn: () => api<Overlay[]>('/overlays'),
  })
  const media = useQuery({
    queryKey: ['media'],
    queryFn: () => api<Page<MediaAsset>>('/media?limit=100'),
  })
  const slides = useMemo(
    () => flattenLayoutVersions(layouts.data),
    [layouts.data]
  )
  const editing = playlists.data?.find((playlist) => playlist.id === editingId)
  const preview = playlists.data?.find((playlist) => playlist.id === previewId)
  const selectedPublishPlaylistId =
    publishPlaylistId ||
    publications.data?.find((publication) => !publication.targeted)
      ?.fallbackPlaylistId ||
    playlists.data?.[0]?.id ||
    ''
  const nextPublicationVersion = (publications.data?.[0]?.version ?? 0) + 1
  const publish = useMutation({
    mutationFn: (fallbackPlaylistId: string) =>
      api<{ version: number }>('/publications/publish', {
        method: 'POST',
        body: jsonBody({ fallbackPlaylistId }),
      }),
    onSuccess: async (publication) => {
      await queryClient.invalidateQueries({ queryKey: ['publications'] })
      toast.success(`Version ${publication.version} published to all screens.`)
    },
    onError: (error) => toast.error(error.message),
  })
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: unknown }) =>
      api(id ? `/playlists/${id}` : '/playlists', {
        method: id ? 'PUT' : 'POST',
        body: jsonBody(body),
      }),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['playlists'] })
      setOpen(false)
      setEditingId(null)
      toast.success(variables.id ? 'Playlist updated.' : 'Playlist created.')
    },
    onError: (error) => toast.error(error.message),
  })
  const startNew = () => {
    setEditingId(null)
    setTickerId('')
    setOverlayId('')
    setLeftOverlayId('')
    setRightOverlayId('')
    setItems([newItem()])
    setOpen(true)
  }
  const startEditing = (playlist: Playlist) => {
    setEditingId(playlist.id)
    setTickerId(playlist.tickerId ?? '')
    setOverlayId(playlist.overlayId ?? '')
    setLeftOverlayId(playlist.leftOverlayId ?? '')
    setRightOverlayId(playlist.rightOverlayId ?? '')
    setItems(
      playlist.items.length
        ? playlist.items.map((item) => ({
            slideVersionId: item.slideVersionId,
            durationMs: item.durationMs,
            transition: item.transition,
            mode: item.mode ?? 'NORMAL',
            repeatEvery: item.repeatEvery ?? null,
            activeStartTime: item.activeStartTime ?? null,
            activeEndTime: item.activeEndTime ?? null,
            activeTimezone: item.activeTimezone ?? null,
          }))
        : [newItem()]
    )
    setOpen(true)
  }
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    save.mutate({
      id: editing?.id,
      body: {
        name: data.get('name'),
        tickerId: tickerId || null,
        overlayId: overlayId || null,
        leftOverlayId: leftOverlayId || null,
        rightOverlayId: rightOverlayId || null,
        items: items.map((item, position) => ({ ...item, position })),
      },
    })
  }
  const updateItem = (index: number, patch: Partial<PlaylistEditorItem>) => {
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      )
    )
  }
  const moveItem = (index: number, direction: -1 | 1) => {
    setItems((current) => {
      const destination = index + direction
      if (destination < 0 || destination >= current.length) return current
      const next = [...current]
      const [item] = next.splice(index, 1)
      next.splice(destination, 0, item)
      return next
    })
  }
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Content'
        title='Playlists'
        description='Choose finished slides, arrange their order, set screen time and transitions, then optionally run one reusable ticker across the playlist.'
        action={
          <div className='flex flex-wrap gap-2'>
            <select
              aria-label='Playlist to publish'
              className='h-9 min-w-48 rounded-md border bg-background px-3 text-sm'
              value={selectedPublishPlaylistId}
              onChange={(event) => setPublishPlaylistId(event.target.value)}
            >
              <option value=''>Select playlist to publish</option>
              {playlists.data?.map((playlist) => (
                <option key={playlist.id} value={playlist.id}>
                  {playlist.name}
                </option>
              ))}
            </select>
            <Button
              type='button'
              variant='outline'
              disabled={!selectedPublishPlaylistId || publish.isPending}
              onClick={() => publish.mutate(selectedPublishPlaylistId)}
            >
              <CloudUpload className='size-4' />
              {publish.isPending
                ? 'Publishing...'
                : `Publish v${nextPublicationVersion}`}
            </Button>
            <Button
              type='button'
              variant='outline'
              onClick={() => void playlists.refetch()}
            >
              <RefreshCw
                className={
                  playlists.isFetching ? 'size-4 animate-spin' : 'size-4'
                }
              />
              Refresh
            </Button>
            <Button onClick={startNew} disabled={!slides.length}>
              <Plus className='size-4' />
              New playlist
            </Button>
          </div>
        }
      />

      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value)
          if (!value) setEditingId(null)
        }}
      >
        <DialogContent className='max-h-[92vh] overflow-y-auto sm:max-w-4xl'>
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Edit playlist' : 'Create playlist'}
            </DialogTitle>
            <DialogDescription>
              Add complete slides only. Media and text stay inside each slide.
            </DialogDescription>
          </DialogHeader>
          <form
            key={editing?.id ?? 'new-playlist'}
            className='space-y-5'
            onSubmit={submit}
          >
            <div className='grid gap-4 sm:grid-cols-2'>
              <div className='space-y-2'>
                <Label htmlFor='playlist-name'>Playlist name</Label>
                <Input
                  id='playlist-name'
                  name='name'
                  defaultValue={editing?.name ?? ''}
                  placeholder='Daily screen loop'
                  required
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='playlist-ticker'>Ticker</Label>
                <select
                  id='playlist-ticker'
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={tickerId}
                  onChange={(event) => setTickerId(event.target.value)}
                >
                  <option value=''>No ticker</option>
                  {tickers.data?.map((ticker) => (
                    <option key={ticker.id} value={ticker.id}>
                      {ticker.name} · {ticker.position.toLowerCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='playlist-left-overlay'>Ticker left panel</Label>
                <select
                  id='playlist-left-overlay'
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={leftOverlayId}
                  disabled={!tickerId}
                  onChange={(event) => setLeftOverlayId(event.target.value)}
                >
                  <option value=''>Off</option>
                  {overlays.data?.map((overlay) => (
                    <option key={overlay.id} value={overlay.id}>
                      {overlay.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='playlist-right-overlay'>
                  Ticker right panel
                </Label>
                <select
                  id='playlist-right-overlay'
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={rightOverlayId}
                  disabled={!tickerId}
                  onChange={(event) => setRightOverlayId(event.target.value)}
                >
                  <option value=''>Off</option>
                  {overlays.data?.map((overlay) => (
                    <option key={overlay.id} value={overlay.id}>
                      {overlay.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className='space-y-2 sm:col-span-2'>
                <Label htmlFor='playlist-overlay'>Corner overlay</Label>
                <select
                  id='playlist-overlay'
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={overlayId}
                  onChange={(event) => setOverlayId(event.target.value)}
                >
                  <option value=''>No corner overlay</option>
                  {overlays.data
                    ?.filter(
                      (overlay) =>
                        overlay.position !== 'TICKER_LEFT' &&
                        overlay.position !== 'TICKER_SIDE'
                    )
                    .map((overlay) => (
                      <option key={overlay.id} value={overlay.id}>
                        {overlay.name} ·{' '}
                        {overlay.position.toLowerCase().replaceAll('_', ' ')}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className='space-y-3'>
              <div className='rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground'>
                Set a slide as{' '}
                <strong className='text-foreground'>Common</strong> to insert it
                after every N regular slides, or as{' '}
                <strong className='text-foreground'>Daily scheduled</strong> so
                it joins the loop only during its daily time window.
              </div>
              <div className='grid grid-cols-[76px_minmax(180px,1fr)_110px_150px_44px] gap-2 px-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
                <span>Order</span>
                <span>Slide</span>
                <span>Seconds</span>
                <span>Transition</span>
                <span />
              </div>
              {items.map((item, index) => (
                <div
                  key={`${index}-${item.slideVersionId}`}
                  className='grid grid-cols-[76px_minmax(180px,1fr)_110px_150px_44px] gap-2 rounded-lg border p-3'
                >
                  <div className='flex gap-1'>
                    <Button
                      type='button'
                      size='icon'
                      variant='ghost'
                      disabled={index === 0}
                      aria-label={`Move slide ${index + 1} up`}
                      onClick={() => moveItem(index, -1)}
                    >
                      <ArrowUp className='size-4' />
                    </Button>
                    <Button
                      type='button'
                      size='icon'
                      variant='ghost'
                      disabled={index === items.length - 1}
                      aria-label={`Move slide ${index + 1} down`}
                      onClick={() => moveItem(index, 1)}
                    >
                      <ArrowDown className='size-4' />
                    </Button>
                  </div>
                  <select
                    aria-label={`Slide ${index + 1}`}
                    className='h-9 rounded-md border bg-background px-2 text-sm'
                    value={item.slideVersionId}
                    required
                    onChange={(event) =>
                      updateItem(index, {
                        slideVersionId: event.target.value,
                      })
                    }
                  >
                    <option value=''>Select slide</option>
                    {slides.map((slide) => (
                      <option key={slide.versionId} value={slide.versionId}>
                        {slide.name} · v{slide.version}
                      </option>
                    ))}
                  </select>
                  <Input
                    aria-label={`Duration ${index + 1} in seconds`}
                    type='number'
                    min={1}
                    max={86400}
                    step={1}
                    value={item.durationMs / 1000}
                    onChange={(event) =>
                      updateItem(index, {
                        durationMs: Number(event.target.value) * 1000,
                      })
                    }
                  />
                  <select
                    aria-label={`Transition ${index + 1}`}
                    className='h-9 rounded-md border bg-background px-2 text-sm'
                    value={item.transition}
                    onChange={(event) =>
                      updateItem(index, {
                        transition: event.target.value as SlideTransition,
                      })
                    }
                  >
                    <option value='NONE'>Cut</option>
                    <option value='FADE'>Fade</option>
                    <option value='SLIDE_LEFT'>Slide left</option>
                    <option value='SLIDE_RIGHT'>Slide right</option>
                    <option value='ZOOM'>Zoom</option>
                    <option value='WIPE'>Wipe</option>
                  </select>
                  <Button
                    type='button'
                    size='icon'
                    variant='ghost'
                    disabled={items.length === 1}
                    aria-label={`Remove slide ${index + 1}`}
                    onClick={() =>
                      setItems((current) =>
                        current.filter((_, itemIndex) => itemIndex !== index)
                      )
                    }
                  >
                    <Trash2 className='size-4' />
                  </Button>
                  <div className='col-span-5 grid gap-3 rounded-md border border-dashed bg-muted/20 p-3 sm:grid-cols-4'>
                    <div className='space-y-1.5'>
                      <Label htmlFor={`slide-mode-${index}`}>Behavior</Label>
                      <select
                        id={`slide-mode-${index}`}
                        aria-label={`Behavior ${index + 1}`}
                        className='h-9 w-full rounded-md border bg-background px-2 text-sm'
                        value={item.mode}
                        onChange={(event) => {
                          const mode = event.target.value as PlaylistItemMode
                          updateItem(index, {
                            mode,
                            repeatEvery:
                              mode === 'COMMON'
                                ? (item.repeatEvery ?? 1)
                                : null,
                            activeStartTime:
                              mode === 'SCHEDULED'
                                ? (item.activeStartTime ?? '09:00')
                                : null,
                            activeEndTime:
                              mode === 'SCHEDULED'
                                ? (item.activeEndTime ?? '17:00')
                                : null,
                            activeTimezone:
                              mode === 'SCHEDULED'
                                ? (item.activeTimezone ?? 'Asia/Dhaka')
                                : null,
                          })
                        }}
                      >
                        <option value='NORMAL'>Regular slide</option>
                        <option
                          value='COMMON'
                          disabled={items.some(
                            (candidate, candidateIndex) =>
                              candidateIndex !== index &&
                              candidate.mode === 'COMMON'
                          )}
                        >
                          Common recurring slide
                        </option>
                        <option value='SCHEDULED'>Daily scheduled slide</option>
                      </select>
                    </div>
                    {item.mode === 'COMMON' ? (
                      <div className='space-y-1.5 sm:col-span-3'>
                        <Label htmlFor={`repeat-every-${index}`}>
                          Show after every N regular slides
                        </Label>
                        <Input
                          id={`repeat-every-${index}`}
                          aria-label={`Repeat interval ${index + 1}`}
                          type='number'
                          min={1}
                          max={100}
                          value={item.repeatEvery ?? 1}
                          onChange={(event) =>
                            updateItem(index, {
                              repeatEvery: Number(event.target.value),
                            })
                          }
                        />
                      </div>
                    ) : null}
                    {item.mode === 'SCHEDULED' ? (
                      <>
                        <div className='space-y-1.5'>
                          <Label htmlFor={`schedule-start-${index}`}>
                            Daily start
                          </Label>
                          <Input
                            id={`schedule-start-${index}`}
                            aria-label={`Schedule start ${index + 1}`}
                            type='time'
                            value={item.activeStartTime ?? '09:00'}
                            onChange={(event) =>
                              updateItem(index, {
                                activeStartTime: event.target.value,
                              })
                            }
                          />
                        </div>
                        <div className='space-y-1.5'>
                          <Label htmlFor={`schedule-end-${index}`}>
                            Daily end
                          </Label>
                          <Input
                            id={`schedule-end-${index}`}
                            aria-label={`Schedule end ${index + 1}`}
                            type='time'
                            value={item.activeEndTime ?? '17:00'}
                            onChange={(event) =>
                              updateItem(index, {
                                activeEndTime: event.target.value,
                              })
                            }
                          />
                        </div>
                        <div className='space-y-1.5'>
                          <Label htmlFor={`schedule-timezone-${index}`}>
                            Timezone
                          </Label>
                          <Input
                            id={`schedule-timezone-${index}`}
                            aria-label={`Schedule timezone ${index + 1}`}
                            value={item.activeTimezone ?? 'Asia/Dhaka'}
                            onChange={(event) =>
                              updateItem(index, {
                                activeTimezone: event.target.value,
                              })
                            }
                          />
                        </div>
                      </>
                    ) : null}
                    {item.mode === 'NORMAL' ? (
                      <p className='self-end pb-2 text-xs text-muted-foreground sm:col-span-3'>
                        Always participates in the playlist loop.
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => setItems((current) => [...current, newItem()])}
              >
                <Plus className='size-4' />
                Add slide
              </Button>
            </div>
            <Button
              className='w-full'
              disabled={
                save.isPending || items.some((item) => !item.slideVersionId)
              }
            >
              {save.isPending
                ? 'Saving…'
                : editing
                  ? 'Save playlist changes'
                  : 'Create playlist'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(preview)}
        onOpenChange={(value) => {
          if (!value) setPreviewId(null)
        }}
      >
        <DialogContent className='sm:max-w-5xl'>
          <DialogHeader>
            <DialogTitle>{preview?.name ?? 'Playlist preview'}</DialogTitle>
            <DialogDescription>
              {preview?.items.length ?? 0} slide(s)
              {preview?.ticker ? ` · ${preview.ticker.name} ticker` : ''}
            </DialogDescription>
          </DialogHeader>
          {preview ? (
            <PlaylistPreview
              playlist={preview}
              slides={slides}
              media={media.data?.items ?? []}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {playlists.isError ? (
        <EmptyState
          title='Could not load playlists'
          description={playlists.error.message}
        />
      ) : playlists.data?.length ? (
        <TableShell
          headers={[
            'Playlist',
            'Slides',
            'Ticker',
            'Overlay',
            'Duration',
            'Actions',
          ]}
        >
          {playlists.data.map((playlist) => (
            <tr key={playlist.id}>
              <td className='px-4 py-3'>
                <div className='font-semibold'>{playlist.name}</div>
                <div className='mt-1 text-xs text-muted-foreground'>
                  {playlist.items
                    .map((item) =>
                      item.mode === 'COMMON'
                        ? `${item.slideName} (common / ${item.repeatEvery})`
                        : item.mode === 'SCHEDULED'
                          ? `${item.slideName} (${item.activeStartTime}–${item.activeEndTime})`
                          : item.slideName
                    )
                    .join(' → ')}
                </div>
              </td>
              <td className='px-4 py-3'>{playlist.items.length}</td>
              <td className='px-4 py-3 text-muted-foreground'>
                {playlist.ticker?.name ?? 'None'}
              </td>
              <td className='px-4 py-3 text-muted-foreground'>
                Left: {playlist.leftOverlay?.name ?? 'Off'} · Right:{' '}
                {playlist.rightOverlay?.name ?? 'Off'}
                {playlist.overlay ? ` · Corner: ${playlist.overlay.name}` : ''}
              </td>
              <td className='px-4 py-3 text-muted-foreground'>
                {Math.round(
                  playlist.items.reduce(
                    (total, item) => total + item.durationMs,
                    0
                  ) / 1000
                )}
                s
              </td>
              <td className='px-4 py-3'>
                <div className='flex gap-2'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => setPreviewId(playlist.id)}
                  >
                    Preview
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => startEditing(playlist)}
                  >
                    <Pencil className='size-4' />
                    Edit
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </TableShell>
      ) : (
        <EmptyState
          title='No playlists yet'
          description='Create slides first, then arrange them into a timed screen loop.'
          action={
            <Button onClick={startNew} disabled={!slides.length}>
              Create playlist
            </Button>
          }
        />
      )}
    </div>
  )
}

type TickerDraftState = Omit<Ticker, 'id' | 'createdAt' | 'updatedAt'>

const defaultPrayerTimes: PrayerTimes = {
  fajr: '05:10',
  sunrise: '06:25',
  dhuhr: '12:15',
  asr: '16:30',
  maghrib: '18:10',
  isha: '19:30',
  jummah: '13:15',
}

const tickerGradientPresets = [
  {
    id: 'SUNSET',
    name: 'Sunset glow',
    colors: ['#f97316', '#dc2626', '#7c3aed'],
  },
  {
    id: 'OCEAN',
    name: 'Ocean blue',
    colors: ['#0ea5e9', '#2563eb', '#06b6d4'],
  },
  {
    id: 'AURORA',
    name: 'Aurora',
    colors: ['#10b981', '#06b6d4', '#8b5cf6'],
  },
  {
    id: 'MIDNIGHT',
    name: 'Midnight neon',
    colors: ['#111827', '#312e81', '#be123c'],
  },
  {
    id: 'GOLD',
    name: 'Golden hour',
    colors: ['#f59e0b', '#ea580c', '#b91c1c'],
  },
] as const

const defaultGradientColors = [...tickerGradientPresets[0].colors]

function newTickerDraft(): TickerDraftState {
  return {
    name: '',
    content: 'Welcome to DOT IT Signage',
    position: 'BOTTOM',
    direction: 'LEFT',
    animation: 'SCROLL',
    rows: [createTickerRow('MESSAGE')],
    rowGap: 0,
    heightPx: 60,
    speed: 70,
    backgroundMode: 'SOLID',
    backgroundColor: '#dc2626',
    gradientColors: defaultGradientColors,
    gradientAngle: 90,
    gradientSpeed: 8,
    textColor: '#ffffff',
    fontSize: 28,
    fontWeight: 700,
    fontFamily: 'Hind Siliguri',
  }
}

function normalizeTicker(ticker: Ticker): TickerDraftState {
  return {
    name: ticker.name,
    content: ticker.content,
    position: ticker.position,
    direction: ticker.direction,
    animation: ticker.animation ?? 'SCROLL',
    rows: ticker.rows?.length
      ? ticker.rows.map((row) => ({
          ...row,
          durationMs: row.durationMs ?? 6000,
        }))
      : [
          {
            ...createTickerRow('MESSAGE'),
            content: ticker.content,
            messages: [ticker.content],
          },
        ],
    rowGap: ticker.rowGap ?? 0,
    heightPx: ticker.heightPx ?? 60,
    speed: ticker.speed,
    backgroundMode: ticker.backgroundMode ?? 'SOLID',
    backgroundColor: ticker.backgroundColor,
    gradientColors:
      ticker.gradientColors?.length >= 2
        ? ticker.gradientColors
        : defaultGradientColors,
    gradientAngle: ticker.gradientAngle ?? 90,
    gradientSpeed: ticker.gradientSpeed ?? 8,
    textColor: ticker.textColor,
    fontSize: ticker.fontSize,
    fontWeight: ticker.fontWeight,
    fontFamily: ticker.fontFamily || 'Hind Siliguri',
  }
}

function TickerRowEditor({
  row,
  index,
  onChange,
  onRemove,
  canRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: {
  row: TickerRow
  index: number
  onChange: (patch: Partial<TickerRow>) => void
  onRemove: () => void
  canRemove: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  canMoveUp: boolean
  canMoveDown: boolean
}) {
  const times = row.prayerTimes ?? defaultPrayerTimes
  const updatePrayer = (key: keyof PrayerTimes, value: string) =>
    onChange({ prayerTimes: { ...times, [key]: value || null } })

  return (
    <section className='space-y-4 rounded-xl border bg-muted/20 p-4'>
      <div className='flex flex-wrap items-end justify-between gap-3'>
        <div>
          <p className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
            Source {index + 1}
          </p>
          <p className='text-sm font-medium'>Dynamic content source</p>
        </div>
        <div className='flex items-end gap-2'>
          <div className='flex gap-1 pb-0.5'>
            <Button
              type='button'
              size='icon'
              variant='outline'
              aria-label={`Move source ${index + 1} up`}
              title='Move source up'
              disabled={!canMoveUp}
              onClick={onMoveUp}
            >
              <ArrowUp className='size-4' />
            </Button>
            <Button
              type='button'
              size='icon'
              variant='outline'
              aria-label={`Move source ${index + 1} down`}
              title='Move source down'
              disabled={!canMoveDown}
              onClick={onMoveDown}
            >
              <ArrowDown className='size-4' />
            </Button>
          </div>
          <div className='w-32 space-y-1'>
            <Label htmlFor={`source-duration-${row.id}`}>Display seconds</Label>
            <Input
              id={`source-duration-${row.id}`}
              type='number'
              min={1}
              max={300}
              value={(row.durationMs ?? 6000) / 1000}
              onChange={(event) =>
                onChange({
                  durationMs: Math.round(Number(event.target.value) * 1000),
                })
              }
            />
          </div>
          <Button
            type='button'
            size='sm'
            variant='ghost'
            disabled={!canRemove}
            onClick={onRemove}
          >
            <Trash2 className='size-4' />
            Remove
          </Button>
        </div>
      </div>
      <div className='grid gap-3 sm:grid-cols-3'>
        <div className='space-y-2'>
          <Label>Source type</Label>
          <select
            className='h-9 w-full rounded-md border bg-background px-3 text-sm'
            value={row.type}
            onChange={(event) => {
              const type = event.target.value as TickerRowType
              const next = createTickerRow(type)
              onChange({ ...next, id: row.id, type })
            }}
          >
            <option value='MESSAGE'>Messages</option>
            <option value='CLOCK'>Live clock</option>
            <option value='DATE'>Live date</option>
            <option value='PRAYER_TIMES'>Prayer times</option>
            <option value='PRAYER_COUNTDOWN'>Next prayer countdown</option>
          </select>
        </div>
        <div className='space-y-2'>
          <Label>Optional label</Label>
          <Input
            value={row.label ?? ''}
            placeholder='News, Dhaka, Masjid…'
            maxLength={80}
            onChange={(event) => onChange({ label: event.target.value })}
          />
        </div>
        <div className='space-y-2'>
          <Label>Separator</Label>
          <Input
            value={row.separator ?? '  |  '}
            maxLength={20}
            onChange={(event) => onChange({ separator: event.target.value })}
          />
        </div>
      </div>

      {row.type === 'MESSAGE' ? (
        <div className='space-y-2'>
          <Label>Messages — one per line</Label>
          <textarea
            className='min-h-28 w-full rounded-md border bg-background px-3 py-2 text-sm'
            value={(row.messages?.length
              ? row.messages
              : [row.content ?? '']
            ).join('\n')}
            maxLength={4000}
            onChange={(event) => {
              const messages = event.target.value.split('\n')
              onChange({
                messages,
                content: messages.join(row.separator || '  |  '),
              })
            }}
          />
          <p className='text-xs text-muted-foreground'>
            All messages repeat in one seamless loop.
          </p>
        </div>
      ) : null}

      {row.type !== 'MESSAGE' ? (
        <div className='grid gap-3 sm:grid-cols-5'>
          <div className='space-y-2'>
            <Label>Timezone</Label>
            <Input
              value={row.timezone ?? 'Asia/Dhaka'}
              placeholder='Asia/Dhaka'
              onChange={(event) => onChange({ timezone: event.target.value })}
            />
          </div>
          <div className='space-y-2'>
            <Label>Locale</Label>
            <select
              className='h-9 w-full rounded-md border bg-background px-3 text-sm'
              value={row.locale ?? 'en-GB'}
              onChange={(event) => onChange({ locale: event.target.value })}
            >
              <option value='en-GB'>English</option>
              <option value='bn-BD'>বাংলা</option>
              <option value='ar-SA'>العربية</option>
            </select>
          </div>
          {row.type === 'CLOCK' ? (
            <>
              <div className='space-y-2'>
                <Label>Hour format</Label>
                <select
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={row.hourCycle ?? 'H12'}
                  onChange={(event) =>
                    onChange({
                      hourCycle: event.target.value as 'H12' | 'H24',
                    })
                  }
                >
                  <option value='H12'>12-hour</option>
                  <option value='H24'>24-hour</option>
                </select>
              </div>
              <div className='space-y-2'>
                <Label>AM/PM display</Label>
                <select
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm disabled:opacity-50'
                  disabled={row.hourCycle === 'H24'}
                  value={row.periodStyle ?? 'LOWER'}
                  onChange={(event) =>
                    onChange({
                      periodStyle: event.target.value as
                        'UPPER' | 'LOWER' | 'HIDE',
                    })
                  }
                >
                  <option value='UPPER'>AM / PM</option>
                  <option value='LOWER'>am / pm</option>
                  <option value='HIDE'>Do not show</option>
                </select>
              </div>
              <label className='flex items-end gap-2 pb-2 text-sm'>
                <input
                  type='checkbox'
                  checked={row.showSeconds !== false}
                  onChange={(event) =>
                    onChange({ showSeconds: event.target.checked })
                  }
                />
                Show seconds
              </label>
            </>
          ) : null}
        </div>
      ) : null}

      {row.type === 'PRAYER_TIMES' || row.type === 'PRAYER_COUNTDOWN' ? (
        <div className='grid gap-3 sm:grid-cols-4'>
          {(
            [
              ['fajr', 'Fajr'],
              ['sunrise', 'Sunrise'],
              ['dhuhr', 'Dhuhr'],
              ['asr', 'Asr'],
              ['maghrib', 'Maghrib'],
              ['isha', 'Isha'],
              ['jummah', 'Jummah'],
            ] as const
          ).map(([key, label]) => (
            <div key={key} className='space-y-2'>
              <Label>{label}</Label>
              <Input
                type='time'
                value={times[key] ?? ''}
                required={!['sunrise', 'jummah'].includes(key)}
                onChange={(event) => updatePrayer(key, event.target.value)}
              />
            </div>
          ))}
        </div>
      ) : null}
    </section>
  )
}

export function TickersPage() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<TickerDraftState>(() => newTickerDraft())
  const tickers = useQuery({
    queryKey: ['tickers'],
    queryFn: () => api<Ticker[]>('/tickers'),
    refetchOnMount: 'always',
  })
  const editing = tickers.data?.find((ticker) => ticker.id === editingId)
  const previewTicker = useMemo<Ticker>(
    () => ({
      id: editingId ?? '00000000-0000-4000-8000-000000000000',
      ...draft,
      createdAt: new Date(0).toISOString(),
      updatedAt: new Date(0).toISOString(),
    }),
    [draft, editingId]
  )
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: unknown }) =>
      api(id ? `/tickers/${id}` : '/tickers', {
        method: id ? 'PUT' : 'POST',
        body: jsonBody(body),
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tickers'] }),
        queryClient.invalidateQueries({ queryKey: ['playlists'] }),
      ])
      setOpen(false)
      setEditingId(null)
      toast.success(variables.id ? 'Ticker updated.' : 'Ticker created.')
    },
    onError: (error) => toast.error(error.message),
  })
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const rows = draft.rows.map((row) => {
      if (row.type !== 'MESSAGE') return row
      const messages = (row.messages ?? [row.content ?? ''])
        .map((message) => message.trim())
        .filter(Boolean)
      return {
        ...row,
        messages,
        content: messages.join(row.separator || '  |  '),
      }
    })
    const fallback =
      rows
        .flatMap((row) => row.messages ?? [row.content ?? ''])
        .find(Boolean) ?? draft.content.trim()
    save.mutate({
      id: editing?.id,
      body: { ...draft, rows, content: fallback },
    })
  }
  const startNew = () => {
    setEditingId(null)
    setDraft(newTickerDraft())
    setOpen(true)
  }
  const startEditing = (ticker: Ticker) => {
    setEditingId(ticker.id)
    setDraft(normalizeTicker(ticker))
    setOpen(true)
  }
  const updateRow = (id: string, patch: Partial<TickerRow>) =>
    setDraft((current) => ({
      ...current,
      rows: current.rows.map((row) =>
        row.id === id ? { ...row, ...patch } : row
      ),
    }))

  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Content'
        title='Dynamic tickers'
        description='Build one clean ticker bar with a fixed logo, smooth center messages, and a fixed live clock. Add dynamic sources for date and prayer information.'
        action={
          <Button onClick={startNew}>
            <Plus className='size-4' />
            New ticker
          </Button>
        }
      />
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value)
          if (!value) setEditingId(null)
        }}
      >
        <DialogContent className='max-h-[94vh] overflow-y-auto sm:max-w-6xl'>
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Edit dynamic ticker' : 'Create dynamic ticker'}
            </DialogTitle>
            <DialogDescription>
              Sources play one after another in order and loop continuously. Add
              as many sources as your channel needs.
            </DialogDescription>
          </DialogHeader>
          <form className='space-y-5' onSubmit={submit}>
            <div className='rounded-xl border bg-background p-3 shadow-sm'>
              <p className='mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
                Live TV preview
              </p>
              <div className='relative aspect-video overflow-hidden rounded-lg bg-zinc-950 p-6'>
                <div className='flex size-full items-center justify-center rounded border border-white/10 text-zinc-500'>
                  Playlist content
                </div>
                <DynamicTickerBar ticker={previewTicker} overlay />
              </div>
            </div>

            <div className='grid gap-4 sm:grid-cols-4'>
              <div className='space-y-2 sm:col-span-2'>
                <Label>Ticker name</Label>
                <Input
                  value={draft.name}
                  placeholder='Daily information channel'
                  required
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label>Position</Label>
                <select
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={draft.position}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      position: event.target.value as 'TOP' | 'BOTTOM',
                    }))
                  }
                >
                  <option value='TOP'>Top</option>
                  <option value='BOTTOM'>Bottom</option>
                </select>
              </div>
              <div className='space-y-2'>
                <Label>Animation</Label>
                <select
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={draft.animation}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      animation: event.target.value as TickerAnimation,
                    }))
                  }
                >
                  <option value='SCROLL'>Smooth continuous</option>
                  <option value='FADE'>Fade</option>
                  <option value='SLIDE'>Slide horizontally</option>
                  <option value='SLIDE_UP'>Slide bottom to top</option>
                  <option value='TYPEWRITER'>Typewriter</option>
                </select>
              </div>
            </div>

            <div className='grid gap-4 sm:grid-cols-4'>
              <div className='space-y-2'>
                <Label>Direction</Label>
                <select
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={draft.direction}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      direction: event.target.value as 'LEFT' | 'RIGHT',
                    }))
                  }
                >
                  <option value='LEFT'>Right to left</option>
                  <option value='RIGHT'>Left to right</option>
                </select>
              </div>
              <div className='space-y-2'>
                <Label>Speed</Label>
                <Input
                  type='number'
                  min={10}
                  max={300}
                  value={draft.speed}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      speed: Number(event.target.value),
                    }))
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label>Font size</Label>
                <Input
                  type='number'
                  min={8}
                  max={240}
                  value={draft.fontSize}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      fontSize: Number(event.target.value),
                    }))
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label>Ticker height (px)</Label>
                <Input
                  type='number'
                  min={40}
                  max={200}
                  value={draft.heightPx}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      heightPx: Number(event.target.value),
                    }))
                  }
                />
              </div>
            </div>

            <section className='space-y-4 rounded-xl border bg-muted/20 p-4'>
              <div>
                <p className='text-sm font-semibold'>Ticker background</p>
                <p className='text-xs text-muted-foreground'>
                  Use one solid color or an animated multi-point gradient.
                </p>
              </div>
              <div className='grid gap-4 sm:grid-cols-4'>
                <div className='space-y-2'>
                  <Label>Background style</Label>
                  <select
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    value={draft.backgroundMode}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        backgroundMode: event.target
                          .value as TickerBackgroundMode,
                      }))
                    }
                  >
                    <option value='SOLID'>Solid color</option>
                    <option value='ANIMATED_GRADIENT'>Animated gradient</option>
                  </select>
                </div>
                {draft.backgroundMode === 'SOLID' ? (
                  <div className='space-y-2'>
                    <Label>Background color</Label>
                    <Input
                      type='color'
                      value={draft.backgroundColor}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          backgroundColor: event.target.value,
                        }))
                      }
                    />
                  </div>
                ) : (
                  <div className='space-y-2'>
                    <Label>Gradient preset</Label>
                    <select
                      className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                      value={
                        tickerGradientPresets.find(
                          (preset) =>
                            preset.colors.join('|') ===
                            draft.gradientColors.join('|')
                        )?.id ?? 'CUSTOM'
                      }
                      onChange={(event) => {
                        const preset = tickerGradientPresets.find(
                          (candidate) => candidate.id === event.target.value
                        )
                        if (preset) {
                          setDraft((current) => ({
                            ...current,
                            gradientColors: [...preset.colors],
                          }))
                        }
                      }}
                    >
                      {tickerGradientPresets.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.name}
                        </option>
                      ))}
                      <option value='CUSTOM'>Custom colors</option>
                    </select>
                  </div>
                )}
                <div className='space-y-2'>
                  <Label>Gradient angle</Label>
                  <Input
                    type='number'
                    min={0}
                    max={360}
                    disabled={draft.backgroundMode === 'SOLID'}
                    value={draft.gradientAngle}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        gradientAngle: Number(event.target.value),
                      }))
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label>Animation seconds</Label>
                  <Input
                    type='number'
                    min={2}
                    max={30}
                    disabled={draft.backgroundMode === 'SOLID'}
                    value={draft.gradientSpeed}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        gradientSpeed: Number(event.target.value),
                      }))
                    }
                  />
                </div>
              </div>
              {draft.backgroundMode === 'ANIMATED_GRADIENT' ? (
                <div className='space-y-2'>
                  <Label>Gradient color points</Label>
                  <div className='flex flex-wrap gap-2'>
                    {draft.gradientColors.map((color, index) => (
                      <div
                        // Keep the native color input mounted while its value changes.
                        // Including `color` in this key closed the picker on every drag.
                        key={`gradient-color-point-${index}`}
                        className='flex items-center gap-1 rounded-lg border bg-background p-1'
                      >
                        <Input
                          aria-label={`Gradient color point ${index + 1}`}
                          className='h-9 w-14 p-1'
                          type='color'
                          value={color}
                          onChange={(event) =>
                            setDraft((current) => ({
                              ...current,
                              gradientColors: current.gradientColors.map(
                                (candidate, colorIndex) =>
                                  colorIndex === index
                                    ? event.target.value
                                    : candidate
                              ),
                            }))
                          }
                        />
                        <Button
                          type='button'
                          size='icon'
                          variant='ghost'
                          aria-label={`Remove gradient color point ${index + 1}`}
                          disabled={draft.gradientColors.length <= 2}
                          onClick={() =>
                            setDraft((current) => ({
                              ...current,
                              gradientColors: current.gradientColors.filter(
                                (_, colorIndex) => colorIndex !== index
                              ),
                            }))
                          }
                        >
                          <Trash2 className='size-4' />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type='button'
                      variant='outline'
                      disabled={draft.gradientColors.length >= 5}
                      onClick={() =>
                        setDraft((current) => ({
                          ...current,
                          gradientColors: [
                            ...current.gradientColors,
                            current.gradientColors.at(-1) ?? '#7c3aed',
                          ],
                        }))
                      }
                    >
                      <Plus className='size-4' />
                      Add color point
                    </Button>
                  </div>
                </div>
              ) : null}
            </section>

            <div className='grid gap-4 sm:grid-cols-3'>
              <div className='space-y-2'>
                <Label>Text color</Label>
                <Input
                  type='color'
                  value={draft.textColor}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      textColor: event.target.value,
                    }))
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label>Font weight</Label>
                <select
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={draft.fontWeight}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      fontWeight: Number(event.target.value),
                    }))
                  }
                >
                  <option value={400}>Regular</option>
                  <option value={600}>Semibold</option>
                  <option value={700}>Bold</option>
                  <option value={900}>Black</option>
                </select>
              </div>
              <div className='space-y-2'>
                <Label>Font family</Label>
                <SignageFontSelect
                  value={draft.fontFamily}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      fontFamily: event.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className='space-y-4'>
              {draft.rows.map((row, index) => (
                <TickerRowEditor
                  key={row.id}
                  row={row}
                  index={index}
                  canRemove={draft.rows.length > 1}
                  canMoveUp={index > 0}
                  canMoveDown={index < draft.rows.length - 1}
                  onChange={(patch) => updateRow(row.id, patch)}
                  onMoveUp={() =>
                    setDraft((current) => {
                      const rows = [...current.rows]
                      ;[rows[index - 1], rows[index]] = [
                        rows[index]!,
                        rows[index - 1]!,
                      ]
                      return { ...current, rows }
                    })
                  }
                  onMoveDown={() =>
                    setDraft((current) => {
                      const rows = [...current.rows]
                      ;[rows[index], rows[index + 1]] = [
                        rows[index + 1]!,
                        rows[index]!,
                      ]
                      return { ...current, rows }
                    })
                  }
                  onRemove={() =>
                    setDraft((current) => ({
                      ...current,
                      rows: current.rows.filter(
                        (candidate) => candidate.id !== row.id
                      ),
                    }))
                  }
                />
              ))}
              <Button
                type='button'
                variant='outline'
                onClick={() =>
                  setDraft((current) => ({
                    ...current,
                    rows: [...current.rows, createTickerRow('MESSAGE')],
                  }))
                }
              >
                <Plus className='size-4' />
                Add content source ({draft.rows.length})
              </Button>
            </div>

            <Button className='w-full' disabled={save.isPending}>
              {save.isPending
                ? 'Saving…'
                : editing
                  ? 'Save ticker changes'
                  : 'Create ticker'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {tickers.data?.length ? (
        <div className='grid gap-4 xl:grid-cols-2'>
          {tickers.data.map((ticker) => (
            <article key={ticker.id} className='rounded-xl border bg-card p-4'>
              <div className='overflow-hidden rounded-lg bg-zinc-950 py-6'>
                <DynamicTickerBar ticker={ticker} />
              </div>
              <div className='mt-4 flex items-start justify-between gap-4'>
                <div>
                  <h2 className='font-semibold'>{ticker.name}</h2>
                  <p className='mt-1 text-xs text-muted-foreground'>
                    {ticker.rows?.length || 1} source(s) ·{' '}
                    {ticker.animation.toLowerCase()} ·{' '}
                    {ticker.position.toLowerCase()} · speed {ticker.speed}
                  </p>
                </div>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => startEditing(ticker)}
                >
                  <Pencil className='size-4' />
                  Edit
                </Button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState
          title='No tickers yet'
          description='Create a reusable dynamic ticker, then select it in a playlist.'
          action={<Button onClick={startNew}>Create ticker</Button>}
        />
      )}
    </div>
  )
}
